import { autoinject, bindable, Container, observable } from "aurelia-framework";
import { NodeModel } from "../../models/node-model";
import {
	ServiceDataItemHandler,
	MeasureService
} from "../measures/services/measure-service";
import { ServiceStructureService } from "../services/service-structure-service";
import {
	DefaultDataItem,
	DefaultServiceDataItemService
} from "./default-service-data-item-service";
import { ScopeTypeGroup } from "./scope-type-group-service";
import { max } from "lodash";
import {
	IServiceDataItems,
	DataItemOption,
	DataItemSelectionResult,
	ReportedServiceDataItemService
} from "../customers/reported-service-data-item-service";
import { IPerformanceIndicatorAM } from "../performance-indicators/performance-indicator-models";
import {
	ReportedServiceDataItemType,
	AttachedDataItem
} from "../reported-service-data-items/reported-service-data-item-models";
import { PerformanceIndicatorService } from "../performance-indicators/performance-indicator-service";
import { ResourceService } from "../../utils/resourceService";
import { NotificationType, ResourcedNotificationService } from "../../components/notifications/notification-service";
import { ApiService } from "../../utils/api-service";

@autoinject
export class DefaultMeasureEdit extends ServiceDataItemHandler {

	@bindable serviceStructureId: string;
	@bindable scopeTypeGroup: ScopeTypeGroup;
	@bindable structureName: string;

	@observable availableDataItems: DataItemOption[];

	services: NodeModel[];
	serviceDataItems: IServiceDataItems = {};
	loading = true;

	constructor(
		private http: ApiService, 
		private service: ServiceStructureService,
		private dataItemService: DefaultServiceDataItemService,
		private reportedServiceDataItemService: ReportedServiceDataItemService,
		private measureService: MeasureService,
		private performanceIndicatorService: PerformanceIndicatorService,
		private resourceService: ResourceService,
		private notificationService: ResourcedNotificationService,
		container: Container
	) {
		super();

		container.registerInstance(ServiceDataItemHandler, this);
	}

	private resolveSortOrder(attachedDataItem: DataItemSelectionResult) {
		const items = this.serviceDataItems[attachedDataItem.serviceId];
		if (items?.length > 0) {
			return max(items.map(i => i.sortOrder)) + 1;
		}
		return 1;
	}

	async bind() {
		const availableMeasures = (await this.measureService.getMeasures()).map(
			m => {
				const item = new DataItemOption(m);
				item.type = ReportedServiceDataItemType.Measure;
				return item;
			}
		);

		const performanceIndicators = (await this.performanceIndicatorService.getPerformanceIndicators()).map(
			(pi: IPerformanceIndicatorAM) => {
				const item = new DataItemOption(pi);
				item.specifier = pi.code;
				item.type = ReportedServiceDataItemType.PerformanceIndicator;
				return item;
			}
		);

		this.availableDataItems = [].concat(
			availableMeasures,
			performanceIndicators
		);

		this.attachDataItem = this.onAttachDataItem;
		this.setInputTypeState = this.onSetInputTypeState;
		this.detachDataItem = this.onDetachDataItem;
		this.changeCustomerComparisonState = this.onChangeCustomerComparisonState;
		//this.changeDescription = this.onChangeDescription;
	}

	async scopeTypeGroupChanged(newValue, oldValue): Promise<any> {
		if (!newValue) {
			return;
		}

		this.loading = true;

		this.services = await this.service.getServiceStructure(
			this.serviceStructureId
		);
		const items = await this.dataItemService.getDefaultServiceDataItemsForServiceStructure(
			this.scopeTypeGroup.id,
			this.serviceStructureId
		);

		this.serviceDataItems = this.createServiceDataItems(items);

		this.loading = false;
	}

	async onAttachDataItem(newDataItem: DataItemSelectionResult): Promise<any> {
		const defaultMeasure = new DefaultDataItem({
			serviceId: newDataItem.serviceId,
			scopeTypeGroupId: this.scopeTypeGroup.id,
			dataItemId: newDataItem.dataItemId,
			sortOrder: this.resolveSortOrder(newDataItem),
			type: newDataItem.type
		});

		await this.dataItemService.addDefaultServiceDataItem(defaultMeasure);
		const updatedMeasureList = await this.dataItemService.getDefaultServiceDataItemsForService(
			this.scopeTypeGroup.id,
			newDataItem.serviceId
		);
		this.serviceDataItems[newDataItem.serviceId] = updatedMeasureList.map(
			i => new AttachedDataItem(i)
		);
	}

	async onDetachDataItem(attachedDataItem: AttachedDataItem) {
		await this.dataItemService.removeDefaultMeasure(attachedDataItem.serviceId, attachedDataItem.id);
		const updatedMeasureList = await this.dataItemService.getDefaultServiceDataItemsForService(
			this.scopeTypeGroup.id,
			attachedDataItem.serviceId
		);
		this.serviceDataItems[
			attachedDataItem.serviceId
		] = updatedMeasureList.map(i => new AttachedDataItem(i));
	}

	public onSetInputTypeState(
		dataItem: AttachedDataItem,
		inputType: string,
		isCollected: boolean
	): Promise<any> {

		if (dataItem.isForCustomerComparison && this.reportedServiceDataItemService.isRemovingLastInputType(dataItem, inputType, isCollected)) {
			return Promise.resolve(new Error(ReportedServiceDataItemService.error_CannotRemoveLastInputFromCustomerComparisonMeasure));
		}

		return this.dataItemService.updateDefaultMeasureInputType(
			dataItem.serviceId,
			dataItem.dataItemId,
			this.scopeTypeGroup.id,
			inputType,
			isCollected
		);
	}

	async onChangeCustomerComparisonState(dataItem: AttachedDataItem): Promise<any> {
		return this.dataItemService.updateDataItemCustomerComparisonState(dataItem);
	}

	async copyToCustomers() {
		var confirmCopyMessage = this.resourceService.getResource("ServiceStructure_Label_ConfirmCopyReportedDataItems");
		if (!confirm(confirmCopyMessage)) {
			return;
		}
			
		try {
			const url = `service-structures/${this.serviceStructureId}/copy-reported-service-data-items-to-customers`;
			const response = await this.http.fetch(url, { method: "post" });
			if (response.status === 200) {
				this.notificationService.push("ServiceStructure_Label_ReportedDataItemsCopySuccess", NotificationType.Success);
			}
		} catch (error) {
			this.notificationService.push("ServiceStructure_Label_ReportedDataItemsCopyError", NotificationType.Error)
			console.error("Error copying reported data items to customers", error);
		}
	}
}
